import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  ShoppingCartVm,
  ShoppingCartItemVm,
  TicketFareShoppingCartItemPayload,
  FeeShoppingCartItemPayload,
  EWalletShoppingCartItemPayload,
  OffenseShoppingCartItemPayload,
} from '@/api';
import shoppingCartService from '@/services/api/shoppingCartService';
import isDefined, { isArrayEmpty } from '@common/utils/isDefined';
import { useUserAccountStore } from '@/store/useUserAccountStore';
import { useToastStore } from '@/store/useToastStore';
import { getEarliestTicketFareValidFrom } from '@/utils/ticketFare';
import { isBefore, startOfDay } from 'date-fns';
import { convertTimeToString, getRemainingTime } from '@/utils/dateTime';
import accountService from '@/services/api/accountService';

export const MIN_DAYS_BETWEEN_TICKET_FARE_VALID_FROM_AND_NOW = 3;

export const useShoppingCartStore = defineStore('shoppingCart', () => {
  const items = ref<ShoppingCartItemVm[]>([]);
  const ticketFareParentIds = ref<string[]>([]);
  const totalPrice = ref(0);
  const itemsCount = ref(0);
  const remainingTime = ref<string | undefined>('00:00:00');
  const expirationTime = ref<string | undefined>(undefined);
  const lifeTimeTimer = ref<NodeJS.Timeout | number | null>(null);

  const isEmpty = computed(() => {
    return isArrayEmpty(items.value);
  });

  const isCardApplicationInShoppingCartForSelectedUser = computed(() => {
    const userAccountStore = useUserAccountStore();
    return items.value.some(item => {
      return (
        (item.payload as FeeShoppingCartItemPayload)?.applicationId &&
        (item.payload as FeeShoppingCartItemPayload)?.originId === userAccountStore.selectedCustomerData.id
      );
    });
  });

  const isCardApplicationInShoppingCart = computed(() => {
    return items.value.some(item => (item.payload as FeeShoppingCartItemPayload)?.applicationId);
  });

  const isReChargeWalletRequestInShoppingCart = computed(() => {
    const userAccountStore = useUserAccountStore();
    return items.value.some(
      item =>
        (item.payload as EWalletShoppingCartItemPayload)?.eWalletId &&
        (item.payload as EWalletShoppingCartItemPayload)?.cardNumber === userAccountStore.cardNumber,
    );
  });

  const isTicketFareInShoppingCart = computed(() => {
    return !!findTicketFareItem.value;
  });

  const findTicketFareItem = computed(() => {
    const userAccountStore = useUserAccountStore();
    return items.value.find(
      item =>
        (item.payload as TicketFareShoppingCartItemPayload)?.ticketFareId &&
        (item.payload as TicketFareShoppingCartItemPayload)?.cardId === userAccountStore.cardDetailData.id,
    );
  });

  const isTicketFareTooSoon = computed(() => {
    const earliestValidFrom = getEarliestTicketFareValidFrom();
    return items.value.some(item => {
      const ticketFareValidFrom = (item.payload as TicketFareShoppingCartItemPayload)?.ticketFareValidFrom;
      if (!(item.payload as TicketFareShoppingCartItemPayload)?.ticketFareId || !ticketFareValidFrom) {
        return false;
      }
      const validFrom = startOfDay(ticketFareValidFrom);
      return isBefore(validFrom, earliestValidFrom);
    });
  });

  const getExpirationTime = computed(() => {
    return expirationTime.value;
  });

  const offenseItems = computed(() => {
    return items.value.filter(item => (item.payload as OffenseShoppingCartItemPayload)?.offenseId);
  });

  const setShoppingCart = (payload: ShoppingCartVm) => {
    items.value = payload.items;
    totalPrice.value = payload.totalPrice;

    ticketFareParentIds.value = [];
    for (const item of payload.items) {
      if (isDefined((item.payload as TicketFareShoppingCartItemPayload)?.ticketFareParentId)) {
        ticketFareParentIds.value.push(
          (item.payload as TicketFareShoppingCartItemPayload).ticketFareParentId as string,
        );
      }
    }
  };

  const setExpirationTime = (value?: string) => {
    expirationTime.value = value;
  };

  const clearLifetimeTimer = () => {
    if (lifeTimeTimer.value) {
      clearInterval(lifeTimeTimer.value);
    }
  };

  const updateRemainingTime = (value?: string) => {
    remainingTime.value = value;
  };

  const setItemsCount = (value?: number) => {
    if (value) {
      itemsCount.value = value;
    }
  };

  const setLifetimeTimer = (timer: NodeJS.Timeout | number | null) => {
    lifeTimeTimer.value = timer;
  };

  const updateShoppingCart = async () => {
    const shoppingCart = await shoppingCartService.getShoppingCart();
    if (shoppingCart) {
      setShoppingCart(shoppingCart);
    }
  };

  const updateItemsCount = async () => {
    const shoppingCartCount = await shoppingCartService.getShoppingCartCount();
    setItemsCount(shoppingCartCount);
  };

  const setCartLifetimeTimer = async () => {
    const toastStore = useToastStore();

    if (getExpirationTime.value) {
      const expirationTime = new Date(getExpirationTime.value).getTime();
      const dateNowForCheck = Date.now();
      const remainingTimeForCheck = expirationTime - dateNowForCheck;

      if (remainingTimeForCheck <= 0) {
        await nullLifetimeAndClearCart();
        toastStore.error({ message: 'Vypršel časový limit košíku. Všechny položky byly odebrány.' });
        return;
      }

      if (lifeTimeTimer.value) {
        clearLifetimeTimer();
      }

      const timer = setInterval(() => {
        const remainingTime = getRemainingTime(expirationTime);

        if (remainingTime <= 0) {
          nullLifetimeAndClearCart();
          toastStore.error({ message: 'Vypršel časový limit košíku. Všechny položky byly odebrány.' });
        } else {
          updateRemainingTime(convertTimeToString(remainingTime));
        }
      }, 1000);

      setLifetimeTimer(timer);
    }
  };

  const nullLifetimeAndClearCart = async () => {
    await nullCartLifetime();
    await shoppingCartService.removeAllItems();
    await updateShoppingCart();
  };

  const nullCartLifetime = async () => {
    clearLifetimeTimer();
    updateRemainingTime('00:00:00');
    await accountService.nullCustomerAccountCartLifeTime();
  };

  const updateLifetimeTimer = () => {
    clearLifetimeTimer();
    setCartLifetimeTimer();
  };

  const isCartExpired = async () => {
    const cartLifetimeResponse = await accountService.getCustomerAccountCartLifetime();

    if (!cartLifetimeResponse.data.cartLifetime) return true;
    else if (cartLifetimeResponse.data.cartLifetime) {
      const cartLifetime = new Date(cartLifetimeResponse.data.cartLifetime).getTime();
      const utcDateNow = Date.now();

      return cartLifetime - utcDateNow <= 0;
    }

    return false;
  };

  const getAndUpdateCartExpirationTime = async () => {
    const response = await accountService.getCustomerAccountCartLifetime();

    setExpirationTime(response.data.cartLifetime);
  };

  return {
    items,
    ticketFareParentIds,
    totalPrice,
    remainingTime,
    expirationTime,
    lifeTimeTimer,

    isEmpty,
    isCardApplicationInShoppingCartForSelectedUser,
    isCardApplicationInShoppingCart,
    isReChargeWalletRequestInShoppingCart,
    isTicketFareInShoppingCart,
    isTicketFareTooSoon,
    offenseItems,
    findTicketFareItem,
    getExpirationTime,
    itemsCount,

    setItemsCount,
    setShoppingCart,
    setExpirationTime,
    setCartLifetimeTimer,
    updateShoppingCart,
    updateLifetimeTimer,
    isCartExpired,
    getAndUpdateCartExpirationTime,
    nullLifetimeAndClearCart,
    nullCartLifetime,
    updateItemsCount,
  };
});
